import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Label, Row, Col, Spinner, Alert,
} from 'reactstrap';
import { useDropzone } from 'react-dropzone';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import {
  getTownhouseUnits,
  createFlushRecord,
  updateFlushRecord,
  uploadFlushAttachment,
  removeFlushAttachment,
  getFlushSignedUrls,
} from './Api';

const ACCEPT = 'image/jpeg,image/jpg,image/png,image/gif,image/webp,image/heic,video/mp4,video/quicktime,video/webm,video/x-msvideo,video/x-matroska';
const MAX_FILE_SIZE = 120 * 1024 * 1024;
const MAX_ATTACHMENTS = 10;
const FLUSH_INTERVAL_YEARS = 2;

export const isVideoKey = (key = '') => /\.(mp4|mov|webm|avi|mkv)$/i.test(key);

export const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';

// Calendar day in the viewer's time zone, as YYYY-MM-DD.
const localDay = (value = new Date()) => {
  const d = new Date(value);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const nextDueFrom = (day) => {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y + FLUSH_INTERVAL_YEARS, m - 1, d);
};

const HotWaterTankFormModal = ({ isOpen, onClose, onSaved, options, record, defaults, userRole }) => {
  const isEdit = !!record?._id;
  // Only the company user may backdate, to log flushes done before today.
  const canSetDate = userRole === 'company';
  const [maintenanceDay, setMaintenanceDay] = useState(localDay());
  const [propertyId, setPropertyId] = useState('');
  const [buildingName, setBuildingName] = useState('');
  const [unitNo, setUnitNo] = useState('');
  const [drained, setDrained] = useState(null);
  const [units, setUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [signedUrls, setSignedUrls] = useState({});
  const [pending, setPending] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setPropertyId(record?.property_details?.property_id || defaults?.propertyId || '');
    setBuildingName(record?.property_details?.building_name || defaults?.buildingName || '');
    setUnitNo(record?.unit_no || '');
    setDrained(typeof record?.drained === 'boolean' ? record.drained : null);
    setAttachments(record?.attachments || []);
    setPending([]);
    setMaintenanceDay(record?.maintenance_date ? localDay(record.maintenance_date) : localDay());
    // Reset only when the modal opens or the record changes, not on every
    // parent render (defaults is a fresh object each time).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, record]);

  const property = options.find(p => p.property_id === propertyId);
  const buildings = property?.buildings || [];

  useEffect(() => {
    if (!isOpen || !propertyId || !buildingName) {
      setUnits([]);
      return;
    }
    let cancelled = false;
    setLoadingUnits(true);
    getTownhouseUnits(propertyId, buildingName)
      .then(list => { if (!cancelled) setUnits(list); })
      .catch(err => { if (!cancelled) { setUnits([]); toast.error(err.message); } })
      .finally(() => { if (!cancelled) setLoadingUnits(false); });
    return () => { cancelled = true; };
  }, [isOpen, propertyId, buildingName]);

  useEffect(() => {
    const missing = attachments.filter(k => !signedUrls[k]);
    if (!missing.length) return;
    getFlushSignedUrls(missing)
      .then(urls => setSignedUrls(prev => ({ ...prev, ...urls })))
      .catch(() => toast.error('Could not load some attachment previews'));
  }, [attachments, signedUrls]);

  // Release pending preview URLs when the modal closes or unmounts.
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  const releasePreviews = () => pendingRef.current.forEach(p => p.preview && URL.revokeObjectURL(p.preview));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!isOpen) releasePreviews(); }, [isOpen]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => releasePreviews, []);

  const onDrop = useCallback((accepted, rejected) => {
    rejected.forEach(({ file, errors }) => errors.forEach(e => {
      if (e.code === 'file-too-large') toast.error(`${file.name} is too large (max 120MB)`);
      else if (e.code === 'file-invalid-type') toast.error(`${file.name}: only pictures and videos`);
    }));
    setPending(prev => {
      const room = MAX_ATTACHMENTS - attachments.length - prev.length;
      if (accepted.length > room) toast.warning(`At most ${MAX_ATTACHMENTS} attachments per record`);
      return [
        ...prev,
        ...accepted.slice(0, Math.max(room, 0)).map(file => ({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          file,
          preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        })),
      ];
    });
  }, [attachments.length]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, accept: ACCEPT, multiple: true, maxSize: MAX_FILE_SIZE, disabled: saving,
  });

  const handleRemoveSaved = async (key) => {
    const result = await Swal.fire({
      title: 'Delete attachment?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete',
    });
    if (!result.isConfirmed) return;
    try {
      const updated = await removeFlushAttachment(record._id, key);
      setAttachments(updated?.attachments || attachments.filter(k => k !== key));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleSave = async () => {
    if (!propertyId || !buildingName) return toast.error('Select a townhouse property and building');
    if (!unitNo.trim()) return toast.error('Unit number is required');
    if (drained === null) return toast.error('Choose whether the hot water tank was drained');

    if (canSetDate && (!maintenanceDay || maintenanceDay > localDay())) {
      return toast.error('Maintenance date cannot be empty or in the future');
    }

    setSaving(true);
    try {
      // Send a date only when it was changed; otherwise the server records it.
      const originalDay = isEdit ? localDay(record.maintenance_date) : localDay();
      const dateChange = canSetDate && maintenanceDay !== originalDay ? { maintenance_date: maintenanceDay } : {};
      const saved = isEdit
        ? await updateFlushRecord(record._id, { unit_no: unitNo.trim(), drained, ...dateChange })
        : await createFlushRecord({
          property_id: propertyId, building_name: buildingName, unit_no: unitNo.trim(), drained, ...dateChange,
        });

      let failed = 0;
      for (const item of pending) {
        try {
          await uploadFlushAttachment(saved._id, item.file);
        } catch (err) {
          failed++;
          toast.error(err.message);
        }
      }
      if (failed) toast.warning(`${failed} attachment(s) failed to upload. Edit the record to try again.`);
      toast.success(isEdit ? 'Record updated' : 'Hot water tank flush recorded');
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const maintenanceDate = isEdit ? formatDate(record.maintenance_date) : formatDate(new Date());
  const dateEdited = isEdit && maintenanceDay !== localDay(record.maintenance_date);
  const nextDue = isEdit && !dateEdited
    ? formatDate(record.next_due_date)
    : formatDate(nextDueFrom(maintenanceDay || localDay()));

  return (
    <Modal isOpen={isOpen} toggle={saving ? undefined : onClose} size="lg" centered scrollable>
      <ModalHeader toggle={saving ? undefined : onClose}>
        {isEdit ? 'Edit Hot Water Tank Flush' : 'Record Hot Water Tank Flush'}
      </ModalHeader>
      <ModalBody>
        {!options.length && (
          <Alert color="warning">No townhouse buildings found. The tracker is only for townhouses.</Alert>
        )}
        <Row className="g-3">
          <Col md={6}>
            <Label className="fw-bold">Property <span className="text-danger">*</span></Label>
            <Input
              type="select"
              value={propertyId}
              disabled={isEdit || saving}
              onChange={e => {
                const next = options.find(p => p.property_id === e.target.value);
                setPropertyId(e.target.value);
                setBuildingName(next?.buildings?.length === 1 ? next.buildings[0] : '');
                setUnitNo('');
              }}
            >
              <option value="">Select property</option>
              {options.map(p => <option key={p.property_id} value={p.property_id}>{p.title}</option>)}
            </Input>
          </Col>
          <Col md={6}>
            <Label className="fw-bold">Townhouse Building <span className="text-danger">*</span></Label>
            <Input
              type="select"
              value={buildingName}
              disabled={isEdit || saving || !propertyId}
              onChange={e => { setBuildingName(e.target.value); setUnitNo(''); }}
            >
              <option value="">Select building</option>
              {buildings.map(b => <option key={b} value={b}>{b}</option>)}
            </Input>
          </Col>
          <Col md={6}>
            <Label className="fw-bold">Unit No. <span className="text-danger">*</span></Label>
            {units.length ? (
              <Input type="select" value={unitNo} disabled={saving} onChange={e => setUnitNo(e.target.value)}>
                <option value="">{loadingUnits ? 'Loading units…' : 'Select unit'}</option>
                {unitNo && !units.includes(unitNo) && <option value={unitNo}>{unitNo}</option>}
                {units.map(u => <option key={u} value={u}>{u}</option>)}
              </Input>
            ) : (
              <Input
                type="text"
                placeholder={loadingUnits ? 'Loading units…' : 'Enter unit number'}
                value={unitNo}
                disabled={saving || !buildingName}
                onChange={e => setUnitNo(e.target.value)}
              />
            )}
          </Col>
          <Col md={6}>
            <Label className="fw-bold d-block">Hot Water Tank Drain <span className="text-danger">*</span></Label>
            <div className="btn-group w-100" role="group">
              <Button
                type="button"
                color={drained === true ? 'success' : 'light'}
                disabled={saving}
                onClick={() => setDrained(true)}
              >
                Yes
              </Button>
              <Button
                type="button"
                color={drained === false ? 'danger' : 'light'}
                disabled={saving}
                onClick={() => setDrained(false)}
              >
                No
              </Button>
            </div>
          </Col>
          <Col md={6}>
            <Label className="fw-bold">Maintenance Date</Label>
            {canSetDate ? (
              <>
                <Input
                  type="date"
                  value={maintenanceDay}
                  max={localDay()}
                  disabled={saving}
                  onChange={e => setMaintenanceDay(e.target.value)}
                />
                <small className="text-muted">Defaults to today. Change it only to log an earlier flush.</small>
              </>
            ) : (
              <>
                <Input type="text" value={maintenanceDate} disabled />
                {!isEdit && <small className="text-muted">Recorded automatically when saved</small>}
              </>
            )}
          </Col>
          <Col md={6}>
            <Label className="fw-bold">Next Due Date</Label>
            <Input type="text" value={nextDue} disabled />
            <small className="text-muted">Every {FLUSH_INTERVAL_YEARS} years from the maintenance date</small>
          </Col>
          <Col xs={12}>
            <Label className="fw-bold">Attachments (Pictures / Videos)</Label>
            <div
              {...getRootProps()}
              className="border border-2 rounded p-3 text-center"
              style={{ borderStyle: 'dashed', cursor: saving ? 'not-allowed' : 'pointer', background: isDragActive ? '#eef6ff' : '#fafbfc' }}
            >
              <input {...getInputProps()} />
              <i className="bx bx-cloud-upload fs-3 d-block text-primary"></i>
              <span className="text-muted">Drop pictures or videos here, or click to choose (max 120MB each)</span>
            </div>
            <div className="d-flex flex-wrap gap-2 mt-3">
              {attachments.map(key => (
                <div key={key} className="position-relative border rounded overflow-hidden" style={{ width: 96, height: 96 }}>
                  {signedUrls[key] ? (
                    <a href={signedUrls[key]} target="_blank" rel="noopener noreferrer">
                      {isVideoKey(key)
                        ? <video src={signedUrls[key]} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted />
                        : <img src={signedUrls[key]} alt="attachment" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                    </a>
                  ) : (
                    <div className="d-flex h-100 align-items-center justify-content-center"><Spinner size="sm" /></div>
                  )}
                  {isEdit && !saving && (
                    <button
                      type="button"
                      className="btn btn-sm btn-danger position-absolute top-0 end-0 py-0 px-1"
                      onClick={() => handleRemoveSaved(key)}
                    >
                      <i className="bx bx-x"></i>
                    </button>
                  )}
                </div>
              ))}
              {pending.map(item => (
                <div key={item.id} className="position-relative border rounded overflow-hidden" style={{ width: 96, height: 96 }}>
                  {item.preview
                    ? <img src={item.preview} alt={item.file.name} style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.7 }} />
                    : <div className="d-flex h-100 align-items-center justify-content-center small text-muted p-1 text-center">
                      <span><i className="bx bx-video d-block fs-4"></i>{item.file.name}</span>
                    </div>}
                  {!saving && (
                    <button
                      type="button"
                      className="btn btn-sm btn-danger position-absolute top-0 end-0 py-0 px-1"
                      onClick={() => {
                        if (item.preview) URL.revokeObjectURL(item.preview);
                        setPending(prev => prev.filter(p => p.id !== item.id));
                      }}
                    >
                      <i className="bx bx-x"></i>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button color="primary" onClick={handleSave} disabled={saving || !options.length}>
          {saving ? <><Spinner size="sm" className="me-2" />Saving…</> : isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export { HotWaterTankFormModal };
